import { motion } from 'framer-motion';

export default function ServicesSection() {
  const mainServices = [
    {
      title: 'イベント事業',
      subtitle: 'Event Dev.',
      description: '各種展示会から地域イベントまで、人を集め、楽しみ、ふれあいを演出する空間づくりを展開しています。',
      features: ['各種展示会の企画・施工', '地域イベント企画・施工', 'ディスプレイ企画・施工', 'イベントプロデュース', '展示ブース設計'],
      icon: 'ri-calendar-event-line',
      color: 'bg-orange-500',
      image: 'https://static.readdy.ai/image/c0a14dc189bb3eb0b6d6716d0af92e52/480d6f7a2b086d9f6552e3dbcf6c6e99.png'
    },
    {
      title: 'サイン制作',
      subtitle: 'Sign Dev.',
      description: '設計・建築段階からの企画・施工まで、視覚的コミュニケーションとしてのサインデザインの力を最大限に発揮します。',
      features: ['サイン企画・立案・制作・施工', '建築サイン計画', 'ビジュアルコミュニケーション', '屋外広告', '案内システム'],
      icon: 'ri-road-map-line',
      color: 'bg-green-500',
      image: 'https://static.readdy.ai/image/c0a14dc189bb3eb0b6d6716d0af92e52/182f6eb1cd5293855c95cd31bb0ad1b8.png'
    },
    {
      title: 'デジタル事業',
      subtitle: 'Digital Dev.',
      description: 'WEBサービス、各種アプリ開発からAI導入コンサルティングまで、デジタル時代に対応した幅広いソリューションを提供します。',
      features: ['Webサービス・アプリ開発', 'デジタルマーケティング', 'SNS計画立案', 'AI導入コンサルティング', 'デジタルコンテンツ企画'],
      icon: 'ri-smartphone-line',
      color: 'bg-purple-500',
      image: 'https://static.readdy.ai/image/c0a14dc189bb3eb0b6d6716d0af92e52/eec997144fc9d0ab08a308f4257ff323.png'
    }
  ];

  const subServices = [
    {
      title: 'デザイン制作',
      description: 'グラフィックデザイン、広告企画、商品開発、パッケージデザインなど',
      icon: 'ri-palette-line',
      color: 'bg-blue-500'
    },
    {
      title: 'マーケティング支援',
      description: 'マーケティング立案、セールスプロモーション、ブランド戦略など',
      icon: 'ri-bar-chart-line',
      color: 'bg-teal-500'
    },
    {
      title: 'コンサルティング',
      description: '広告調査・コンサルティング、市場分析、戦略立案など',
      icon: 'ri-lightbulb-line',
      color: 'bg-indigo-500'
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">サービス一覧</h2>
          <p className="text-xl text-gray-600">幅広い分野で企業様のニーズにお応えします</p>
        </motion.div>

        {/* メイン事業 */}
        <div className="mb-16">
          <motion.h3 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-bold text-center text-gray-900 mb-8"
          >
            メイン事業
          </motion.h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {mainServices.map((service, index) => (
              <motion.div 
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"
              >
                <div className="relative h-48">
                  <img 
                    src={typeof service.image === 'string' && service.image.startsWith('https://') 
                      ? service.image 
                      : `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7Bservice.image%7D&width=400&height=300&seq=main-service-${index}&orientation=landscape`}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  <div className={`absolute top-4 left-4 ${service.color} rounded-full p-3`}>
                    <i className={`${service.icon} text-white text-xl`}></i>
                  </div>
                </div>
                
                <div className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h4>
                  <p className="text-sm text-blue-600 mb-3">{service.subtitle}</p>
                  <p className="text-gray-700 text-sm leading-relaxed mb-4">{service.description}</p>
                  
                  <div className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600">
                        <i className="ri-check-line text-green-500 text-base mr-2"></i>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* その他の事業 */}
        <div>
          <motion.h3 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-bold text-center text-gray-900 mb-8"
          >
            その他の事業
          </motion.h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {subServices.map((service, index) => (
              <motion.div 
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className={`${service.color} rounded-full p-3 mr-4`}>
                    <i className={`${service.icon} text-white text-lg`}></i>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">{service.title}</h4>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
